import type { MessageSentimentCondition as MessageSentimentConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { ConversationResolutionSentiment } from "@redotech/redo-model/conversation-insights";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const sentimentDisplayNames: Record<ConversationResolutionSentiment, string> = {
  [ConversationResolutionSentiment.POSITIVE]: "Positive",
  [ConversationResolutionSentiment.NEUTRAL]: "Neutral",
  [ConversationResolutionSentiment.NEGATIVE]: "Negative",
};

const sentimentItems: RedoListItem<ConversationResolutionSentiment>[] =
  Object.values(ConversationResolutionSentiment).map((sentiment) => ({
    id: sentiment,
    type: "text" as const,
    text: sentimentDisplayNames[sentiment],
    value: sentiment,
  }));

export const MessageSentimentCondition = memo(
  function MessageSentimentCondition({
    condition,
    onChange,
    editableFields,
  }: {
    condition: MessageSentimentConditionType;
    onChange: (updated: MessageSentimentConditionType) => void;
    editableFields?: ReadonlySet<string>;
  }) {
    const sentimentsEditable =
      editableFields === undefined || editableFields.has("sentiments");

    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const selectedItems: RedoListItem<ConversationResolutionSentiment>[] =
      useMemo(() => {
        return (condition.sentiments || []).map((sentiment) => ({
          id: sentiment,
          type: "text" as const,
          text: sentimentDisplayNames[sentiment],
          value: sentiment,
        }));
      }, [condition.sentiments]);

    const displayItem:
      | RedoListItem<ConversationResolutionSentiment>
      | undefined = useMemo(() => {
      if (selectedItems.length === 0) return undefined;
      if (selectedItems.length === 1) return selectedItems[0];
      return {
        id: "display",
        type: "text" as const,
        text: `${selectedItems.length} sentiments selected`,
        value: ConversationResolutionSentiment.POSITIVE,
      };
    }, [selectedItems]);

    return (
      <Flex dir="column" gap="sm" w="full">
        <Text fontSize="xs" textColor="tertiary">
          Sentiment
        </Text>
        <RedoSingleSelectDropdownInputButton
          disabled={!sentimentsEditable}
          isDropdownOpen={dropdownOpen}
          onClick={() => sentimentsEditable && setDropdownOpen(!dropdownOpen)}
          placeholder="Select sentiments"
          ref={setDropdownButtonRef}
          selectedItem={displayItem}
          size="xs"
        />
        <RedoMultiselectDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor
          items={sentimentItems}
          selectedItems={selectedItems}
          selectionVariant="checkbox"
          setDropdownOpen={setDropdownOpen}
          setSelectedItems={(items) => {
            if (!sentimentsEditable) return;
            onChange({
              ...condition,
              sentiments: items.map((item) => item.value),
            });
          }}
          size="xs"
        />
      </Flex>
    );
  },
);
